import * as React from 'react';
import { Box, Button, Typography, Stack, useTheme, CircularProgress, TextField } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { appPaths } from '../../app-paths';
import { LogoWithText } from '../../components/logo-with-text';
import { useAlert } from '@dwelvio/react-alert';
import { useAuth } from '@dwelvio/auth-react';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const { show } = useAlert();
  const theme = useTheme();
  const { resendVerificationEmail, verifyEmail } = useAuth();
  const [params] = useSearchParams();
  const [isResending, setIsResending] = React.useState(false);
  const [countdown, setCountdown] = React.useState(30);
  const [canResend, setCanResend] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);

  const { register, handleSubmit, setValue, watch } = useForm<{ code: string }>({
    defaultValues: { code: params.get('code') || '' },
  });
  const codeValue = watch('code');

  const email = params.get('email');

  // Countdown timer for resend button
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleResendVerification = async () => {
    if (!email) {
      show.error('E-mailová adresa nie je k dispozícii');
      return;
    }

    setIsResending(true);
    setCanResend(false);
    setCountdown(30);

    try {
      const result = await resendVerificationEmail(email);
      if (result === 'success') {
        show.info('Overovací e-mail bol znovu odoslaný');
      } else {
        show.error('Nepodarilo sa odoslať overovací e-mail');
      }
    } catch (error) {
      show.error('Nastala chyba pri odosielaní e-mailu');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerification = async (username: string, code: string) => {
    if (!username) {
      show.error('E-mailová adresa nie je k dispozícii');
      return;
    }

    if (!code || code.length !== 6) {
      show.error('Prosím zadajte 6-ciferný kód');
      return;
    }

    setIsVerifying(true);

    try {
      const result = await verifyEmail(username, code);
      if (result === 'success') {
        await navigate(appPaths.signin.navigate({ signup: true, username: username || undefined }));
      } else {
        show.error('Nepodarilo sa overiť e-mail. Kód môže byť neplatný alebo expirovaný.');
      }
    } catch (error) {
      show.error('Nastala chyba pri overovaní e-mailu');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box
      maxWidth="500px"
      minWidth="300px"
      sx={{ mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100vh' }}
    >
      <Box
        component="header"
        sx={{ mx: 'auto', py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <LogoWithText color={theme.palette.primary.main} textColor={theme.palette.secondary.dark} height={50} />
      </Box>
      <Box component="main" sx={{ mx: 'auto', py: 3, px: 2 }}>
        <Stack gap={3} sx={{ textAlign: 'center' }}>
          <Typography variant="h4">Overte svoj e-mail</Typography>

          <Typography variant="body1">
            Na váš email {email && <strong>{email}</strong>} sme odoslali správu s 6-ciferným overovacím kódom, ktorý je
            potrebné zadať do formulára nižšie. Kód je platný 24 hodín.
          </Typography>

          <Box component="main" sx={{ mx: 'auto', py: 3, px: 2 }}>
            <Stack gap={3}>
              <form onSubmit={handleSubmit((data) => handleVerification(email || '', data.code))}>
                <Stack gap={2}>
                  <TextField
                    fullWidth
                    label="Zadajte overovací kód"
                    {...register('code')}
                    onChange={(e) => setValue('code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    value={codeValue}
                    placeholder="000000"
                    autoFocus
                    autoComplete="one-time-code"
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                      style: { letterSpacing: '0.5em', fontSize: '1.5rem', textAlign: 'center' },
                    }}
                    disabled={isVerifying}
                  />

                  <Button fullWidth variant="contained" type="submit" disabled={isVerifying || codeValue.length !== 6}>
                    {isVerifying ? 'Overujem...' : 'Overiť e-mail'}
                  </Button>
                </Stack>
              </form>
              <Typography variant="body1">
                V prípade, ak ste email s kódom nedostali, môžete si ho nechať znovu poslať kliknutím na tlačidlo
                nižšie. Pred opätovným odoslaním skontrolujte zadanú emailovú adresu a priečinok nevyžiadanej pošty.
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                disabled={!canResend || isResending}
                onClick={handleResendVerification}
                startIcon={isResending ? <CircularProgress size={20} /> : undefined}
              >
                {isResending ? 'Odosielanie...' : canResend ? 'Odoslať kód znovu' : `Odoslať znovu (${countdown}s)`}
              </Button>
            </Stack>
          </Box>

          <Stack gap={2} sx={{ mt: 2 }}>
            <Button variant="text" onClick={() => navigate(appPaths.signin.navigate())}>
              Späť na prihlásenie
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Box component="footer" sx={{ py: 3 }}>
        <Typography variant="body2" textAlign="center">
          © dwelvio.com {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );
}
